import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import { defaultFullscreenPageStyling, styleInputDefault } from "@/theme";
import ChevronDownIcon from "@/theme/icons/ChevronDownIcon";
import { convertPxToRem, formatAmount } from "@/utils";
import {
  Autocomplete,
  Divider,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

export default function DocMedsDeliveryInfo() {
  const { t } = useTranslation();
  const cannotDeliverToAddress = false;
  const price = 24900;

  return (
    <Stack sx={{ ...defaultFullscreenPageStyling }} spacing={1}>
      <CircledBackButton showLabel />
      <Stack spacing={4}>
        <Stack alignItems="center">
          <IconLoader
            icon="ChatWithMapAndUnuIcon"
            sx={{
              fontSize: convertPxToRem(80),
            }}
          />
          <Typography component="h1" variant="h2">
            {t("DocMedsDeliveryInfo.title")}
          </Typography>
        </Stack>
        <Stack spacing={2}>
          <Typography component="h2" variant="h3" fontWeight="bold">
            {t("DocMedsDeliveryInfo.subtitle")}
          </Typography>
          <Stack
            flexDirection="row"
            gap={2}
            sx={{ backgroundColor: "accent2.100", padding: 2, borderRadius: 2 }}
          >
            <IconLoader
              icon="InformationSymbolFilledCircleIcon"
              color="primary"
            />
            <Typography component="p" variant="body2">
              <Trans i18nKey="DocMedsDeliveryInfo.info" />
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <InputLabel htmlFor={t("DocMedsDeliveryInfo.addressInput.label")}>
            <Typography variant="body2" color="neutral.main" fontWeight="bold">
              {t("DocMedsDeliveryInfo.addressInput.label")}
            </Typography>
          </InputLabel>
          <Autocomplete
            id={t("DocMedsDeliveryInfo.addressInput.label")}
            freeSolo
            options={["An address"]}
            sx={{ backgroundColor: "background.paper" }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  input: {
                    ...styleInputDefault,
                    fontSize: `${convertPxToRem(14)} !important`,
                  },
                }}
                placeholder={t("DocMedsDeliveryInfo.addressInput.placeholder")}
              />
            )}
          />
        </Stack>
        {cannotDeliverToAddress && (
          <Stack
            sx={{ p: 2, backgroundColor: "background.paper", borderRadius: 2 }}
            spacing={1}
          >
            <Typography variant="body1" fontWeight="bold">
              {t("DocMedsDeliveryInfo.cannotDeliver.title")}
            </Typography>
            <Typography variant="body2">
              <Trans i18nKey="DocMedsDeliveryInfo.cannotDeliver.body" />
            </Typography>
            <Box
              sx={{
                p: 1,
                backgroundColor: "primary.light",
                borderRadius: 2,
                width: "fit-content",
              }}
            >
              <Typography
                variant="body1"
                color="primary.contrastText"
                fontWeight="bold"
              >
                <Trans
                  i18nKey="DocMedsDeliveryInfo.cannotDeliver.price"
                  values={{ price: formatAmount(price, "ZAR") }}
                />
              </Typography>
            </Box>
          </Stack>
        )}

        {!cannotDeliverToAddress && (
          <>
            <Divider />
            <Stack spacing={1}>
              <InputLabel
                htmlFor={t("DocMedsDeliveryInfo.dependantSelection.ariaLabel")}
              >
                <Typography
                  variant="body2"
                  color="neutral.main"
                  fontWeight="bold"
                >
                  {t("DocMedsDeliveryInfo.dependantSelection.label")}
                </Typography>
              </InputLabel>
              <Select
                fullWidth
                displayEmpty
                IconComponent={ChevronDownIcon}
                sx={{
                  backgroundColor: "background.paper",
                  height: convertPxToRem(52),
                }}
                id={t("DocMedsDeliveryInfo.dependantSelection.ariaLabel")}
                inputProps={{
                  "aria-label": t(
                    "DocMedsDeliveryInfo.dependantSelection.ariaLabel"
                  ),
                }}
                MenuProps={{
                  "aria-label": t(
                    "DocMedsDeliveryInfo.dependantSelection.ariaLabel"
                  ),
                }}
                onChange={() => {}}
                renderValue={() => (
                  <Typography
                    color="neutral.light"
                    sx={{ fontSize: convertPxToRem(14) }}
                  >
                    {t("DocMedsDeliveryInfo.dependantSelection.placeholder")}
                  </Typography>
                )}
              >
                <MenuItem>
                  <Stack textAlign="left">Joe</Stack>
                </MenuItem>
              </Select>
            </Stack>
          </>
        )}

        <Stack>
          {cannotDeliverToAddress ? (
            <Stack direction="row" gap={2}>
              <ButtonWithAnalytics
                fullWidth
                page="DocMedsDeliveryInfo"
                intent="navigational"
                text={t("common.goHomeButton")}
                variant="outlined"
              >
                {t("common.goHomeButton")}
              </ButtonWithAnalytics>
              <ButtonWithAnalytics
                fullWidth
                page="DocMedsDeliveryInfo"
                intent="navigational"
                text={t("common.yesPlease")}
              >
                {t("common.yesPlease")}
              </ButtonWithAnalytics>
            </Stack>
          ) : (
            <ButtonWithAnalytics
              page="DocMedsDeliveryInfo"
              intent="navigational"
              text={t("common.nextButton")}
            >
              {t("common.nextButton")}
            </ButtonWithAnalytics>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
